import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectTourniquetIdentity, tourniquetIdentityActions } from '../../store/entities/tourniquetIdentity';
import { selectTourniquetTrainings, tourniquetTrainingsActions } from '../../store/entities/tourniquetTrainings';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { TourniquetButtonContent } from './TourniquetButtonContent';
import { TourniquetCaption } from './TourniquetCaption';
import { TourniquetInvitationIssueSupplier } from './TourniquetInvitationIssueSupplier';
import { TourniquetInvitationOk } from './TourniquetInvitationOk';
import { TourniquetInvitationIssueCompany } from './TourniquetInvitationIssueCompany';
import { TourniquetInvitationTrainings } from './TourniquetInvitationTrainings';
import { TourniquetCard, TourniquetContent, TourniquetHeader } from './TourniquetStyles';
import styled from '@emotion/styled';
import { QcsErrorCode } from '@api/models/enums/QcsErrorCode';
import { ActionRequiredBy } from '@api/models/enums/ActionRequiredBy';
import { invitationService, visitationService } from '@api/services/services';
import { EnteringBeforeEarliestEntryTime } from './EnteringBeforeEarliestEntryTime';
import { VisitationEntryDto } from '@api/models/visitations/VisitationEntryDto';
import { resources } from '@api/resources';

const MobileHeader = styled.div(({ theme }) => ({
  display: 'block',
  marginBottom: '1rem',

  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const DesktopHeader = styled(TourniquetHeader)(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.up('md')]: {
    display: 'grid',
  },
}));

export const TourniquetInvitation: FC = () => {
  const { t } = useTranslation();
  const tourniquetIdentity = useAppSelector(selectTourniquetIdentity);
  const tourniquetTrainings = useAppSelector(selectTourniquetTrainings);
  const dispatch = useAppDispatch();
  const invitation = tourniquetIdentity.invitation!;

  const personEmployeeId = tourniquetIdentity.employeeId;
  const person = invitation?.persons?.find((p) => p.employeeId === personEmployeeId);

  const handleAbort = () => {
    if (tourniquetIdentity.invitations && tourniquetIdentity.invitations.length > 1) {
      dispatch(
        tourniquetIdentityActions.success({
          ...tourniquetIdentity,
          invitation: undefined,
        }),
      );
      return;
    }
    dispatch(tourniquetIdentityActions.reset());
  };

  const handleReload = async () => {
    const data: VisitationEntryDto = {
      invitation: invitation.id ?? '',
      pin: tourniquetIdentity.pin ?? '',
    };

    const resp = await invitationService.getById(invitation.id!);

    const incompleteTrainingsRes = await visitationService.incompleteTrainings(data);

    const validateEntry = await visitationService.accessPointValidateEntry(data);

    dispatch(tourniquetTrainingsActions.success(incompleteTrainingsRes.data));
    dispatch(
      tourniquetIdentityActions.success({
        ...tourniquetIdentity,
        invitation: resp.data,
        invitations: tourniquetIdentity.invitations?.map((x) => (x.id === invitation.id ? resp.data : x)),
        validationErrors: validateEntry.data,
      }),
    );
  };

  useEffect(() => {
    handleReload();
  }, []);

  const errors = useMemo(() => {
    return tourniquetIdentity.validationErrors;
  }, [tourniquetIdentity.validationErrors]);

  const title = useMemo(() => {
    if (!errors || errors.length === 0) return 'tourniquet.noIssueTitle';

    if (errors.length === 1 && errors[0].code === QcsErrorCode.InvalidTraining) {
      return 'tourniquet.issueTrainingsTitle';
    }

    return 'tourniquet.issueCompanyTitle';
  }, [errors]);

  const issue = useMemo(() => {
    return !(!errors || errors.length === 0);
  }, [errors]);

  const issueCompany = useMemo(() => {
    return errors && errors.some((e) => e.actionRequiredFrom === ActionRequiredBy.COMPANY);
  }, [errors]);

  const issueSupplier = useMemo(() => {
    return (
      errors &&
      errors.some(
        (e) =>
          e.actionRequiredFrom === ActionRequiredBy.SUPPLIER &&
          e.code !== QcsErrorCode.InvalidTraining &&
          e.code !== QcsErrorCode.EnteringBeforeEarliestEntryTime,
      )
    );
  }, [errors]);

  const cantEnterYetError = useMemo(() => {
    return (
      errors && errors.find((e) => e.actionRequiredFrom === ActionRequiredBy.SUPPLIER && e.code === QcsErrorCode.EnteringBeforeEarliestEntryTime)
    );
  }, [errors]);

  const issueTrainings = useMemo(() => {
    return errors && errors.some((e) => e.code === QcsErrorCode.InvalidTraining);
  }, [errors]);

  const caption = useMemo(() => <TourniquetCaption title={t(title)} />, [t, title]);

  const invitationName = useMemo(
    () => title === 'tourniquet.issueCompanyTitle' && <TourniquetCaption title={invitation.name ?? ''} />,
    [invitation.name, title],
  );

  return (
    <TourniquetButtonContent backOnClickMobile={handleAbort}>
      <TourniquetCard>
        <TourniquetContent>
          <MobileHeader>
            {caption}
            <div
              style={{
                marginTop: '1rem',
              }}
            >
              {invitationName}
            </div>
            {issue && (
              <div
                style={{
                  width: 'max-content',
                  marginLeft: 'auto',
                  marginTop: '0.2rem',
                }}
              >
                <QcsLoadingButton
                  variant="contained"
                  onClick={handleReload}
                  resourceLoading={[resources.incompleteTrainings, resources.getInvitationById, resources.accessPointValidateEntry]}
                >
                  {t('tourniquet.checkAgain')}
                </QcsLoadingButton>
              </div>
            )}
          </MobileHeader>
          <DesktopHeader>
            <QcsButton variant="outlined" onClick={handleAbort}>
              {t('common.back')}
            </QcsButton>
            {caption}
            {issue && (
              <QcsLoadingButton
                variant="contained"
                onClick={handleReload}
                resourceLoading={[resources.incompleteTrainings, resources.getInvitationById, resources.accessPointValidateEntry]}
                sx={{ width: '100%' }}
              >
                {t('tourniquet.checkAgain')}
              </QcsLoadingButton>
            )}
            <div />
            {invitationName}
          </DesktopHeader>

          {cantEnterYetError && <EnteringBeforeEarliestEntryTime error={cantEnterYetError} />}
          {issueSupplier && <TourniquetInvitationIssueSupplier invitation={invitation} />}
          {issueCompany && <TourniquetInvitationIssueCompany invitation={invitation} />}
          {issueTrainings && <TourniquetInvitationTrainings invitation={invitation} invitationPerson={tourniquetTrainings.invitationPerson} />}
          {!issue && <TourniquetInvitationOk invitation={invitation} person={person!} />}
        </TourniquetContent>
      </TourniquetCard>
    </TourniquetButtonContent>
  );
};
